import React, { useEffect, useMemo, useState } from "react";
import { Content } from "antd/es/layout/layout";
import "./style.css";
import { Button, Modal, Spin } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LeftOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchUserAttributes } from "@aws-amplify/auth";
import { signedPost } from "../Services/awsSignedFetch";
import { generateClient } from "aws-amplify/api";
import AppActionModal from "../Components/AppActionModal";

const client = generateClient();

const GET_ADMIN_FEES = /* GraphQL */ `
  query GetAdminFees {
    listAdmins(limit: 1) {
      items {
        id
        walletTransactionFeeCAD
        walletTransactionFeeBBD
      }
    }
  }
`;

const GET_USER_MARKUP_FEES = /* GraphQL */ `
  query GetUserMarkupFees($userID: ID!) {
    userMarkupsByUserID(userID: $userID, limit: 1) {
      items {
        id
        walletTransactionFeeCAD
        walletTransactionFeeBBD
      }
    }
  }
`;

const Overview = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [user, setUser] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertState, setAlertState] = useState({
    type: "",
    message: "",
    show: false,
  });
  const [seconds, setSeconds] = useState(state?.selectTime || 300);
  const [showRateInLockPill, setShowRateInLockPill] = useState(true);
  const [transactionFee, setTransactionFee] = useState(null);

  const recipient = state?.recipient;
  const currencyNow = state?.currencyNow || "cad";
  const currencyNow2 = state?.currencyNow2 || "bbd";
  const sourceCurrencyCode = String(currencyNow).toUpperCase();
  const targetCurrencyCode = String(currencyNow2).toUpperCase();
  const isSameCurrencyTransfer = sourceCurrencyCode === targetCurrencyCode;
  const isWalletTransfer =
    Boolean(recipient?.recipientWalletID) ||
    recipient?.recipientDeliveryMethod === "BajCan Wallet";
  const sourceOfFund = state?.sourceOfFund || "N/A";
  const formattedSendRate = Number(state?.formattedSendRate || 0);
  const selectedPaymentMethod = state?.selectedPaymentMethod || "N/A";
  const selectedReasons = state?.selectedReasons || "N/A";
  const cadToBBDRate = Number(state?.cadToBBDRate || 0);
  const getRate = Number(state?.getRate || 0);
  const feeAmount = Number(state?.feeAmount || 0);

  useEffect(() => {
    if (!state || isSameCurrencyTransfer) return;
    const interval = setInterval(() => {
      setSeconds((prevSeconds) => {
        if (prevSeconds <= 1) {
          clearInterval(interval);
          navigate("/sendMoney", {
            state: { ...state, recipient },
          });
          return 0;
        }
        return prevSeconds - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isSameCurrencyTransfer, navigate, recipient, state]);

  useEffect(() => {
    if (isSameCurrencyTransfer) return;
    const interval = setInterval(() => {
      setShowRateInLockPill((prev) => !prev);
    }, 10000);
    return () => clearInterval(interval);
  }, [isSameCurrencyTransfer]);

  useEffect(() => {
    const currentAuthenticatedUser = async () => {
      try {
        const currentUser = await fetchUserAttributes();
        setUser(currentUser);
      } catch (err) {
        console.log(err);
      }
    };
    currentAuthenticatedUser();
  }, []);

  useEffect(() => {
    if (!isSameCurrencyTransfer || !isWalletTransfer || !user["custom:TableID"])
      return;
    const fetchFees = async () => {
      try {
        const [adminRes, markupRes] = await Promise.all([
          client.graphql({ query: GET_ADMIN_FEES }),
          client.graphql({
            query: GET_USER_MARKUP_FEES,
            variables: { userID: user["custom:TableID"] },
          }),
        ]);
        const admin = adminRes.data?.listAdmins?.items?.[0] || {};
        const markup = markupRes.data?.userMarkupsByUserID?.items?.[0] || {};
        const adminFee =
          sourceCurrencyCode === "CAD"
            ? Number(admin.walletTransactionFeeCAD || 0)
            : Number(admin.walletTransactionFeeBBD || 0);
        const userFee =
          sourceCurrencyCode === "CAD"
            ? Number(markup.walletTransactionFeeCAD || 0)
            : Number(markup.walletTransactionFeeBBD || 0);
        setTransactionFee(
          Number((adminFee + userFee).toFixed(2)),
        );
      } catch (err) {
        console.error("Failed to fetch transaction fee:", JSON.stringify(err?.errors || err?.message || err, null, 2));
      }
    };
    fetchFees();
  }, [isSameCurrencyTransfer, isWalletTransfer, user, sourceCurrencyCode]);

  const formattedTime = useMemo(() => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  }, [seconds]);

  const formatNumber = (num) =>
    Number(num || 0)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const formatRate = (num) =>
    Number(num || 0)
      .toFixed(4)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const formatPhoneNumber = (value) => {
    const digits = (value || "").replace(/\D/g, "");
    if (!digits) return "N/A";
    if (digits.startsWith("1246") && digits.length === 11) {
      const local = digits.slice(4);
      return `+1 246 ${local.slice(0, 3)}-${local.slice(3)}`;
    }
    if (digits.startsWith("1") && digits.length === 11) {
      const local = digits.slice(1);
      return `+1 (${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
    }
    if (digits.length === 7) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return value || "N/A";
  };

  const triggerPaymentMethodLambda = async () => {
    setIsModalVisible(true);
    setLoading(true);
    try {
      const isWalletTransfer =
        Boolean(recipient?.recipientWalletID) ||
        recipient?.recipientDeliveryMethod === "BajCan Wallet";

      // console.log("[SendMoney] payload:", {
      //   isWalletTransfer, // true = wallet transfer, false = remittance
      //   senderUserID: user["custom:TableID"],
      //   sourceCurrency: sourceCurrencyCode,
      //   amount: formattedSendRate,
      //   recipientID: recipient.id,
      //   recipientWalletID: recipient.recipientWalletID,
      //   recipientDeliveryMethod: recipient.recipientDeliveryMethod,
      //   message: selectedReasons,
      //   sourceOfFund,
      // });

      const { lambdaStatus, data } = await signedPost(
        "https://40euekjuk6.execute-api.us-east-2.amazonaws.com/default/SendMoney-staging",
        {
          action: "CREATE_REQUEST",
          isWalletTransfer,
          senderUserID: user["custom:TableID"],
          sourceCurrency: sourceCurrencyCode,
          amount: formattedSendRate,
          recipientID: recipient.id,
          message: selectedReasons || undefined,
          ...(!isWalletTransfer && {
            sourceOfFund: sourceOfFund || undefined,
            deliveryMethod: recipient.recipientDeliveryMethod || undefined,
          }),
        },
      );

      console.log("[SendMoney] lambdaStatus:", lambdaStatus, "data:", data);

      if (lambdaStatus < 200 || lambdaStatus >= 300) {
        throw new Error(data?.error || "Payment request failed.");
      }

      setAlertState({
        type: "success",
        message: "Payment completed successfully.",
        show: true,
      });
      setIsResultModalOpen(true);
    } catch (error) {
      console.error("Payment error:", error);
      setAlertState({
        type: "error",
        message: "An error occurred during the payment process.",
        show: true,
      });
      setIsResultModalOpen(true);
    } finally {
      setLoading(false);
      setIsModalVisible(false);
    }
  };

  const recipientName =
    `${recipient?.firstName || ""} ${recipient?.lastName || ""}`.trim();
  const recipientAddress = [
    recipient?.addressStreet,
    recipient?.addressLine2,
    recipient?.addressCity,
    recipient?.addressState,
    recipient?.addressCountry,
    recipient?.addressZipCode,
  ]
    .filter(Boolean)
    .join(", ");
  const paymentMethodLabel =
    selectedPaymentMethod === "interac"
      ? "Interac"
      : selectedPaymentMethod === "wallet"
        ? "Wallet"
        : selectedPaymentMethod;
  const isSuccess = alertState.type === "success";

  if (!state || !recipient) {
    return (
      <Content className="appPageContent">
        <div className="appPageShell">
          <div className="appSectionHeader">
            <h2 className="appSectionTitle">Overview</h2>
          </div>
          <div className="appTableCard">
            <p>Transfer details are missing. Please restart from Send Money.</p>
            <Button type="primary" onClick={() => navigate("/recipients")}>
              Back to Recipients
            </Button>
          </div>
        </div>
      </Content>
    );
  }

  return (
    <Content className="appPageContent">
      <div className="appPageShell appPageShellNarrow overviewShell">
        <div className="appSectionHeader appPageHeader">
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={() =>
              navigate("/sendMoney", {
                state: {
                  ...state,
                  selectTime: isSameCurrencyTransfer ? null : seconds,
                },
              })
            }
            className="recipientDetailBackBtn"
          />
          <h2 className="appSectionTitle">Overview</h2>
        </div>

        <div className="appTableCard overviewCard">
          <div className="overviewSection">
            <div className="overviewSectionHeader">
              <h3>Transfer Summary</h3>
              {!isSameCurrencyTransfer ? (
                <div className="sendMoneyV2RateActions overviewRateActions">
                  <span className="sendMoneyRateLockComboPill">
                    <span className="sendMoneyRateLockComboIcon" aria-hidden="true">
                      <LockOutlined />
                    </span>
                    <span
                      className="sendMoneyRateLockComboIconDivider"
                      aria-hidden="true"
                    />
                    <span className="sendMoneyRateLockComboContent">
                      <span
                        className={`sendMoneyRateLockComboFace ${
                          showRateInLockPill
                            ? "sendMoneyRateLockComboFaceActive"
                            : ""
                        }`}
                      >
                        {`1 ${sourceCurrencyCode} = ${formatRate(cadToBBDRate)} ${targetCurrencyCode}`}
                      </span>
                      <span
                        className={`sendMoneyRateLockComboFace ${
                          !showRateInLockPill
                            ? "sendMoneyRateLockComboFaceActive"
                            : ""
                        }`}
                      >
                        {`Rate locked ${formattedTime}`}
                      </span>
                    </span>
                  </span>
                </div>
              ) : null}
            </div>

            <div className="overviewRow">
              <span>Transfer Amount</span>
              <strong>
                ${formatNumber(formattedSendRate)} {sourceCurrencyCode}
              </strong>
            </div>
            <div className="overviewRow">
              <span>Recipient Gets</span>
              <strong>
                $
                {formatNumber(
                  isSameCurrencyTransfer && isWalletTransfer
                    ? formattedSendRate
                    : getRate,
                )}{" "}
                {targetCurrencyCode}
              </strong>
            </div>
            <div className="overviewRow">
              <span>Fee</span>
              <strong>
                {isSameCurrencyTransfer && isWalletTransfer
                  ? transactionFee === null
                    ? "Loading..."
                    : `$${formatNumber(transactionFee)} ${sourceCurrencyCode}`
                  : `$${formatNumber(feeAmount)} ${sourceCurrencyCode}`}
              </strong>
            </div>
            {isSameCurrencyTransfer && isWalletTransfer && (
              <div className="overviewRow">
                <span>Total You Pay</span>
                <strong>
                  {transactionFee === null
                    ? "Loading..."
                    : `$${formatNumber(formattedSendRate + (transactionFee ?? 0))} ${sourceCurrencyCode}`}
                </strong>
              </div>
            )}
            <div className="overviewRow">
              <span>Source of Funds</span>
              <strong>{sourceOfFund}</strong>
            </div>
            <div className="overviewRow">
              <span>Sending Purpose</span>
              <strong>{selectedReasons}</strong>
            </div>
          </div>

          <div className="overviewSection">
            <div className="overviewSectionHeader">
              <h3>Recipient Details</h3>
            </div>

            <div className="overviewRow">
              <span>Name</span>
              <strong>{recipientName || "N/A"}</strong>
            </div>
            <div className="overviewRow">
              <span>Phone</span>
              <strong>{formatPhoneNumber(recipient.phoneNumber)}</strong>
            </div>
            <div className="overviewRow">
              <span>Email</span>
              <strong>{recipient.email || "N/A"}</strong>
            </div>
            <div className="overviewRow">
              <span>Address</span>
              <strong>{recipientAddress || "N/A"}</strong>
            </div>
            <div className="overviewRow">
              <span>Delivery Method</span>
              <strong>{recipient.recipientDeliveryMethod || "N/A"}</strong>
            </div>
          </div>

          <div className="overviewSection">
            <div className="overviewSectionHeader">
              <h3>Payment Details</h3>
            </div>
            <div className="overviewRow">
              <span>Payment Method</span>
              <strong>{paymentMethodLabel}</strong>
            </div>
          </div>

          <Button
            onClick={triggerPaymentMethodLambda}
            className="signupbtn overviewConfirmBtn"
            size="large"
            block
          >
            Confirm and Send
          </Button>
        </div>
      </div>

      <Modal
        title="Processing Payment"
        open={isModalVisible}
        footer={null}
        closable={false}
        maskClosable={false}
      >
        {loading ? <Spin tip="Loading..." /> : null}
      </Modal>

      <AppActionModal
        open={isResultModalOpen}
        onCancel={() => setIsResultModalOpen(false)}
        className="overviewResultModal"
        variant={isSuccess ? "success" : "error"}
        icon={isSuccess ? <CheckCircleFilled /> : <CloseCircleFilled />}
        title={isSuccess ? "Transfer Sent" : "Transfer Failed"}
        description={alertState.message}
        closable={false}
        maskClosable={false}
        primaryAction={{
          label: isSuccess ? "Back to Recipients" : "Close",
          onClick: () => {
            setIsResultModalOpen(false);
            if (isSuccess) {
              navigate("/recipients");
              return;
            }
            setAlertState({ type: "", message: "", show: false });
          },
          className: "overviewResultBtn",
        }}
      />
    </Content>
  );
};

export default Overview;
